import React, { useContext, useMemo } from "react";
import { Alert, Box } from "@mui/material";
import { AsIsViewerContainer } from "../../components/as_is_viewer/container";
import { LoadingState } from "../../components/transformed_custom_view/viewer/LoadingState";
import { useApi } from "../../contexts/app/provider";
import { VersionedDataContext } from "../../contexts/versioned_data/context";
import { useGetMyOutputDatastructureNamesQuery } from "../../services/api/__generated__/backend_gateway-types";

const PO_MODIFICATION_DATASET_CANDIDATES = [
  "po_modifications",
  "po_modification_results",
];

export const PoModificationsContainer = () => {
  const { apiService } = useApi();
  const { mainRun, lastSuccessfulRun } = useContext(VersionedDataContext);
  const activeRun = mainRun ?? lastSuccessfulRun;
  const outputId = activeRun?.runSettings
    ? `${activeRun.runSettings.inputId}|${activeRun.runSettings.settingsNameAndVersion}`
    : null;

  const { data, loading, error } = useGetMyOutputDatastructureNamesQuery({
    client: apiService.getClient(),
    variables: { outputId: outputId || "" },
    skip: !outputId,
    errorPolicy: "all",
    fetchPolicy: "cache-and-network",
  });

  const availableDatasets = data?.getMyOutputDatastructureNames ?? [];

  const datasetName = useMemo(() => {
    for (const candidate of PO_MODIFICATION_DATASET_CANDIDATES) {
      if (availableDatasets.includes(candidate)) {
        return candidate;
      }
    }

    return (
      availableDatasets.find((name) => name.toLowerCase().includes("po_mod")) ||
      null
    );
  }, [availableDatasets]);

  if (!outputId) {
    return (
      <Box p={2}>
        <Alert severity="info">
          Select a completed run to view persisted PO modifications.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return <LoadingState />;
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error">
          Failed to load available output datasets for this run: {error.message}
        </Alert>
      </Box>
    );
  }

  if (!datasetName) {
    return (
      <Box p={2}>
        <Alert severity="warning">
          No PO modifications parquet was found for this run.
          {availableDatasets.length > 0
            ? ` Available outputs: ${availableDatasets.join(", ")}`
            : ""}
        </Alert>
      </Box>
    );
  }

  return (
    <AsIsViewerContainer
      datasetName={datasetName}
      folder="outputs"
      setId={outputId}
      format="parquet"
    />
  );
};
